import _ from 'lodash';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useImperativeHandle, useMemo } from 'react';
import { useMaPlanFilter } from './api/use-ma-plan-filter';
import { usePartDConfirm } from './ui/part-d-confirm';
import { useDsnpOffConfirm } from './ui/dsnp-off-confirm';
import { useDsnpOnConfirm } from './ui/dsnp-on-confirm';
import {
  MedicareQuoteQuery,
  useMaPlansInfoQuery,
} from '@coverright/data-access/medicare';
import { FiltersDto, MedicareQuoteFilterExtraBenefit } from '@coverright/data-access/types/medicare';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MaFiltersForm {
  maxOutOfPocketRangeNames: string[];
  maxCostRangeNames: string[];
  rating: string[];
  planTypes: string[];
  SNPTypes: string[];
  drugCoverage: string[];
  extraBenefits: string[];
  companies: string[];
  showTopDoctors: boolean;
  showTopDrugs: boolean;
  partBPremiumReduction: string[];
}

export interface UseMaFiltersOptions {
  quote: MedicareQuoteQuery['medicareQuote'];
  onChange: (values: Partial<FiltersDto>) => void;
}

export interface UseMaFiltersReturn {
  values: MaFiltersForm;
  filters: ReturnType<typeof useMaPlanFilter>;
  toggleValue: (field: keyof MaFiltersForm, name: string | string[], value: boolean) => Promise<void>;
  reset: () => void;
  doctorsCheckBoxMessage?: string;
  drugsCheckBoxMessage?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const MA_FILTER_TITLES = [
  'Max out-of-pocket',
  'Estimated average total cost per month',
  '',
  'Plan type',
  'Special needs plans',
  'Prescription Drug Coverage',
  'Part B Premium Reduction',
  'Company',
  'Additional benefits',
];

export const MA_FILTER_SKELETON_LINES: Record<keyof ReturnType<typeof useMaPlanFilter>, number> = {
  maxOutOfPocketRangeNames: 4,
  maxCostRangeNames: 4,
  rating: 0,
  planTypes: 3,
  SNPTypes: 1,
  extraBenefits: 6,
  companies: 6,
  partBPremiumReduction: 1,
  drugCoverage: 1,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const convertMaFilterValues = (values: MaFiltersForm): Partial<FiltersDto> => ({
  ..._.omit(values, ['drugCoverage', 'partBPremiumReduction']) as any,
  extraBenefits: _.uniq(_.compact([
    ...values.extraBenefits,
    values.drugCoverage.length ? MedicareQuoteFilterExtraBenefit.DrugCoverage : undefined,
    values.partBPremiumReduction.length ? MedicareQuoteFilterExtraBenefit.PartBPremiumGiveback : undefined,
  ])),
});

const schemaFields = {
  planTypes: yup.array(),
  SNPTypes: yup.array(),
  extraBenefits: yup.array(),
  companies: yup.array(),
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useMaFilters({ quote, onChange }: UseMaFiltersOptions): UseMaFiltersReturn {
  const filterValues = quote?.filters;
  const { data: info } = useMaPlansInfoQuery({ fetchPolicy: 'cache-only' });
  const doctorsCheckBoxMessage = info?.maPlansInfo?.doctorsCheckBoxMessage;
  const drugsCheckBoxMessage = info?.maPlansInfo?.drugsCheckBoxMessage;

  const formik = useFormik<MaFiltersForm>({
    initialValues: {
      maxOutOfPocketRangeNames: filterValues?.maxOutOfPocketRangeNames || [],
      maxCostRangeNames: filterValues?.maxCostRangeNames || [],
      rating: filterValues?.rating || [],
      planTypes: filterValues?.planTypes || [],
      SNPTypes: filterValues?.SNPTypes || [],
      extraBenefits: _.without(
        filterValues?.extraBenefits,
        MedicareQuoteFilterExtraBenefit.DrugCoverage,
        MedicareQuoteFilterExtraBenefit.PartBPremiumGiveback,
      ) || [],
      companies: filterValues?.companies || [],
      showTopDoctors: filterValues?.showTopDoctors || false,
      showTopDrugs: filterValues?.showTopDrugs || false,
      partBPremiumReduction: filterValues?.extraBenefits.filter(
        v => v === MedicareQuoteFilterExtraBenefit.PartBPremiumGiveback
      ) || [],
      drugCoverage: filterValues?.extraBenefits.filter(
        v => v === MedicareQuoteFilterExtraBenefit.DrugCoverage
      ) || [],
    },
    validationSchema: yup.object(schemaFields),
    onSubmit: () => {},
  });

  const selectedFilters = useMemo(() => convertMaFilterValues(formik.values), [formik.values]);
  const filters = useMaPlanFilter(quote, selectedFilters);

  const partDConfirm = usePartDConfirm();
  const dsnpOffConfirm = useDsnpOffConfirm();
  const dsnpOnConfirm = useDsnpOnConfirm();

  const toggleValue = async (
    fieldName: keyof MaFiltersForm,
    name: string | string[],
    value: boolean,
  ) => {
    if (fieldName === 'drugCoverage' && value === false) {
      const result = await partDConfirm();
      if (!result) return;
    }

    if (fieldName === 'SNPTypes') {
      const result = value ? await dsnpOnConfirm() : await dsnpOffConfirm();
      if (!result) return;
    }

    let newValue: any = value;

    if (Array.isArray(formik.values[fieldName])) {
      const currentArray = formik.values[fieldName] as string[];
      const namesToRemove = Array.isArray(name) ? name : [name];
      const arrayWithoutValue = _.without(currentArray, ...namesToRemove);
      newValue = value ? [...arrayWithoutValue, ...namesToRemove] : arrayWithoutValue;
    }

    const newValues = { ...formik.values, [fieldName]: newValue };

    if (!doctorsCheckBoxMessage && newValues.showTopDoctors) newValues['showTopDoctors'] = false;
    if (!drugsCheckBoxMessage && newValues.showTopDrugs) newValues['showTopDrugs'] = false;

    formik.setFieldValue(fieldName, newValue);
    onChange(convertMaFilterValues(newValues));
  };

  const reset = () => {
    const values: MaFiltersForm = {
      maxOutOfPocketRangeNames: [],
      maxCostRangeNames: [],
      rating: [],
      planTypes: [],
      SNPTypes: [],
      extraBenefits: [MedicareQuoteFilterExtraBenefit.DrugCoverage],
      companies: [],
      showTopDoctors: false,
      showTopDrugs: false,
      partBPremiumReduction: [],
      drugCoverage: [MedicareQuoteFilterExtraBenefit.DrugCoverage],
    };
    formik.setValues(values);
    onChange(convertMaFilterValues(values));
  };

  return {
    values: formik.values,
    filters,
    toggleValue,
    reset,
    doctorsCheckBoxMessage: doctorsCheckBoxMessage ?? undefined,
    drugsCheckBoxMessage: drugsCheckBoxMessage ?? undefined,
  };
}
